import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { FileText, ArrowLeft, Loader2, AlertCircle, Edit, Users, Info } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RFQView = () => {
  const navigate = useNavigate();
  const { rfq_number } = useParams();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('header');
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [rfqData, setRfqData] = useState(null);

  useEffect(() => {
    if (rfq_number) {
      loadRFQData();
    }
  // eslint-disable-next-line
  }, [rfq_number]);

  const loadRFQData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/rfqs/${rfq_number}`);
      setRfqData(response.data);
      setActiveItemIndex(0);
    } catch (error) {
      console.error('Error loading RFQ:', error);
      toast.error('Failed to load RFQ');
      navigate('/p2p/rfq-list');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'OPEN': 'bg-orange-100 text-orange-800 border-orange-200',
      'IN_PROGRESS': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'CLOSED': 'bg-green-100 text-green-800 border-green-200',
      'CANCELLED': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          <span className="ml-3 text-lg">Loading RFQ...</span>
        </div>
      </Layout>
    );
  }

  if (!rfqData || !rfqData.items || rfqData.items.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">RFQ Not Found</h2>
            <p className="text-gray-600 mb-4">The RFQ you're looking for doesn't exist or has no items.</p>
            <Button onClick={() => navigate('/p2p/rfq-list')}>Back to RFQ List</Button>
          </div>
        </div>
      </Layout>
    );
  }

  const currentItem = rfqData.items[activeItemIndex];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/p2p/rfq-list')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <FileText className="h-6 w-6" />
                View Request for Quotation
              </h1>
              <p className="text-sm text-gray-500">RFQ Number: {rfq_number}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${getStatusColor(rfqData.status)}`}>
              {rfqData.status.replace(/_/g, ' ')}
            </span>
            {rfqData.status !== 'CLOSED' && rfqData.status !== 'CANCELLED' && (
              <Button variant="outline" onClick={() => navigate(`/p2p/rfq-edit/${rfq_number}`)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="header">RFQ Header</TabsTrigger>
            <TabsTrigger value="items">Item Details</TabsTrigger>
            <TabsTrigger value="vendors">Vendor Assignment</TabsTrigger>
          </TabsList>

          {/* TAB 1: RFQ HEADER */}
          <TabsContent value="header">
            <Card>
              <CardHeader>
                <CardTitle>RFQ Header - General Information</CardTitle>
                <CardDescription>Document control and organizational details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Document Type</p>
                    <p className="font-medium text-lg">{rfqData.document_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Language</p>
                    <p className="font-medium text-lg">{rfqData.language}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">RFQ Date</p>
                    <p className="font-medium text-lg">{formatDate(rfqData.rfq_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Quotation Deadline</p>
                    <p className="font-medium text-lg text-red-600">{formatDate(rfqData.quotation_deadline)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Purchasing Organization</p>
                    <p className="font-medium text-lg">{rfqData.purchasing_organization}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Purchasing Group</p>
                    <p className="font-medium text-lg">{rfqData.purchasing_group}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Company Code</p>
                    <p className="font-medium text-lg">{rfqData.company_code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Items</p>
                    <p className="font-medium text-lg">{rfqData.total_items}</p>
                  </div>
                  {rfqData.collective_rfq_number && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Collective RFQ Number</p>
                      <p className="font-medium text-lg">{rfqData.collective_rfq_number}</p>
                    </div>
                  )}
                  {rfqData.description && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-600 mb-1">Description</p>
                      <p className="font-medium text-lg">{rfqData.description}</p>
                    </div>
                  )}
                  {rfqData.reference_number && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Reference Number</p>
                      <p className="font-medium text-lg">{rfqData.reference_number}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Created By</p>
                    <p className="font-medium text-lg">{rfqData.created_by}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Created At</p>
                    <p className="font-medium text-lg">{formatDate(rfqData.created_at)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 2: ITEM DETAILS */}
          <TabsContent value="items">
            <Card>
              <CardHeader>
                <CardTitle>RFQ Line Items</CardTitle>
                <CardDescription>Item details and specifications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {rfqData.items.map((item, index) => (
                    <Button
                      key={`item-tab-${item.item_number || index}`}
                      type="button"
                      variant={activeItemIndex === index ? "default" : "outline"}
                      size="sm"
                      onClick={() => setActiveItemIndex(index)}
                    >
                      Item {item.item_number}
                    </Button>
                  ))}
                </div>

                {currentItem && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
                    <div className="md:col-span-3">
                      <div className="bg-orange-50 p-3 rounded-md flex items-start gap-2">
                        <Info className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-orange-800">
                          Viewing Item {currentItem.item_number}. Vendor details are available in the "Vendor Assignment" tab.
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Item Number</p>
                      <p className="font-medium">{currentItem.item_number}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Item Category</p>
                      <p className="font-medium">{currentItem.item_category || 'Standard'}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Material</p>
                      <p className="font-medium">{currentItem.material_id || '-'}</p>
                    </div>

                    <div className="md:col-span-3">
                      <p className="text-sm text-gray-600 mb-1">Short Text</p>
                      <p className="font-medium">{currentItem.short_text}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Quantity</p>
                      <p className="font-medium">{currentItem.quantity} {currentItem.unit_of_measure}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Unit of Measure</p>
                      <p className="font-medium">{currentItem.unit_of_measure}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Delivery Date</p>
                      <p className="font-medium">{formatDate(currentItem.delivery_date)}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Delivery Date Category</p>
                      <p className="font-medium">
                        {currentItem.delivery_date_category === 'D' ? 'Day' : 
                         currentItem.delivery_date_category === 'W' ? 'Week' : 
                         currentItem.delivery_date_category === 'M' ? 'Month' : '-'}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Plant</p>
                      <p className="font-medium">{currentItem.plant}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Storage Location</p>
                      <p className="font-medium">{currentItem.storage_location || '-'}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Material Group</p>
                      <p className="font-medium">{currentItem.material_group || '-'}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Target Price</p>
                      <p className="font-medium">{currentItem.target_price ? `$${currentItem.target_price}` : '-'}</p>
                    </div>

                    {currentItem.pr_number && (
                      <>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">PR Number</p>
                          <p className="font-medium">{currentItem.pr_number}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">PR Item</p>
                          <p className="font-medium">{currentItem.pr_item || '-'}</p>
                        </div>
                      </>
                    )}

                    {currentItem.info_text && (
                      <div className="md:col-span-3">
                        <p className="text-sm text-gray-600 mb-1">Info Text</p>
                        <p className="font-medium bg-gray-50 p-3 rounded">{currentItem.info_text}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 3: VENDOR ASSIGNMENT */}
          <TabsContent value="vendors">
            <Card>
              <CardHeader>
                <CardTitle>Vendor Assignment</CardTitle>
                <CardDescription>Vendors assigned to each item and their quotation status</CardDescription>
              </CardHeader>
              <CardContent>
                {rfqData.items.map((item, itemIndex) => (
                  <div key={`vendor-assign-${item.item_number || itemIndex}`} className="mb-6 pb-6 border-b last:border-b-0">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-lg">
                        Item {item.item_number}: {item.short_text}
                      </h3>
                      <span className="text-sm text-gray-600">
                        {item.vendors.length} vendor(s) assigned
                      </span>
                    </div>
                    {item.vendors.length > 0 ? (
                      <div className="space-y-2">
                        {item.vendors.map((vendor, vendorIndex) => (
                          <div key={`${item.item_number || itemIndex}-${vendor.vendor_number || vendorIndex}`} className="p-4 bg-gray-50 rounded-md">
                            <div className="flex items-start gap-3">
                              <Users className="h-5 w-5 text-gray-500 mt-1" />
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="font-medium text-lg">{vendor.vendor_number}</p>
                                  <span className={`px-2 py-1 rounded text-xs font-semibold
                                    ${vendor.quotation_status === 'A' ? 'bg-yellow-100 text-yellow-800' : 
                                      vendor.quotation_status === 'B' ? 'bg-green-100 text-green-800' : 
                                      'bg-red-100 text-red-800'}`}>
                                    {vendor.quotation_status === 'A' ? 'Awaiting' : 
                                     vendor.quotation_status === 'B' ? 'Received' : 'Rejected'}
                                  </span>
                                </div>
                                
                                {vendor.quotation_status === 'B' && (
                                  <div className="grid grid-cols-3 gap-4 mt-2">
                                    {vendor.quoted_price && (
                                      <div>
                                        <p className="text-xs text-gray-600">Quoted Price</p>
                                        <p className="font-medium text-green-600">${vendor.quoted_price} {vendor.quoted_currency}</p>
                                      </div>
                                    )}
                                    {vendor.quoted_delivery_date && (
                                      <div>
                                        <p className="text-xs text-gray-600">Delivery Date</p>
                                        <p className="font-medium">{formatDate(vendor.quoted_delivery_date)}</p>
                                      </div>
                                    )}
                                    {vendor.response_date && (
                                      <div>
                                        <p className="text-xs text-gray-600">Response Date</p>
                                        <p className="font-medium">{formatDate(vendor.response_date)}</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {vendor.remarks && (
                                  <div className="mt-2">
                                    <p className="text-xs text-gray-600">Remarks</p>
                                    <p className="text-sm mt-1">{vendor.remarks}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded">No vendors assigned</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default RFQView;
